import {
  DatasetData,
  DatasetDataRowV2,
  DatasetDataType
} from "@/types/dataset";
import { MarkerPinItem } from "@/types/map";
import { CatchmentDataset, CatchmentDatasetConfig } from "@/types/spatial";

import { v4 as uuidv4 } from "uuid";
import wellknown from "wellknown";

import { isOrdinalType } from "./dataset";

export function getVisibleCatchmentDatasets(
  datasets: CatchmentDatasetConfig["datasets"]
) {
  return Object.values(datasets).filter(
    (entry) => entry.dataset?.properties?.isVisible
  );
}

export function getRankingMap(
  rawDataset: DatasetData,
  labelAttributeName: string,
  rankConfig?: { maxRanking: number; isEnabled: boolean }
) {
  if (
    !rawDataset?.columns?.length ||
    !rawDataset?.rows?.length ||
    !rankConfig?.isEnabled
  )
    return {};

  const labelColumn = rawDataset.columns.find(
    (col) => col.name === labelAttributeName
  );

  if (!labelColumn || isOrdinalType(labelColumn.data_type)) {
    return {};
  }

  const labelIndex = rawDataset.columns.findIndex(
    (col) => col.name === labelAttributeName
  );

  const values = rawDataset.rows
    .map((row) => {
      const value = Number(row[labelIndex]);
      const label = `${row[labelIndex]}` || "";
      return { label, value };
    })
    .filter((v) => !Number.isNaN(v.value));

  const maxRanking = rankConfig?.maxRanking ?? 10;
  const sorted = [...values]
    .sort((a, b) => b.value - a.value)
    .slice(0, maxRanking);

  const rankMap: Record<string, Record<string, number>> = {
    [labelAttributeName]: {}
  };

  for (const [i, item] of sorted.entries()) {
    const rank = i + 1;
    if (rankConfig?.maxRanking && rank > rankConfig?.maxRanking) return;
    rankMap[labelAttributeName][item.label] = rank;
  }

  return rankMap;
}

function handleRow(
  row: DatasetDataRowV2,
  pointIndex: number,
  selectedAttributeIndex: number,
  index: number,
  callback: (
    long: number,
    lat: number,
    label: string,
    index: number,
    rank?: number
  ) => void,
  rankMap?: Record<string, Record<string, number>>,
  attributeName?: string
) {
  const pointWKT = row[pointIndex];
  if (typeof pointWKT !== "string") return;

  const geometry = wellknown.parse(pointWKT);
  if (!geometry) return;

  const label = `${row[selectedAttributeIndex]}` || `Marker #${index + 1}`;

  if (geometry?.type === "Point") {
    const [long, lat] = geometry.coordinates;

    const rank = attributeName ? rankMap?.[attributeName]?.[label] : undefined;
    callback(long, lat, label, index, rank);
  }
}

export function iterateRows(
  datasetValues: CatchmentDataset[],
  callback: (
    long: number,
    lat: number,
    label: string,
    index: number,
    rank?: number
  ) => void
) {
  for (const entry of datasetValues) {
    const { dataset, rawDataset } = entry;
    const attributeName = dataset?.selectedCategory?.attribute_name;

    const pointIndex = rawDataset.columns.findIndex(
      (v) => v.data_type === DatasetDataType.TYPE_GEOMETRY
    );

    if (pointIndex < 0) return;

    const selectedAttributeIndex = rawDataset.columns.findIndex(
      (v) => v.name === attributeName
    );

    const rankMap = getRankingMap(
      rawDataset,
      attributeName,
      dataset.style?.rankConfig
    );

    for (const [index, row] of rawDataset.rows.entries()) {
      handleRow(
        row,
        pointIndex,
        selectedAttributeIndex,
        index,
        callback,
        rankMap,
        attributeName
      );
    }
  }
}

export function createMarkersFromDataset(
  datasetValues: CatchmentDataset[],
  onFeature: (
    label: string,
    coordinates: [number, number],
    id: string,
    rank?: number
  ) => void
) {
  iterateRows(datasetValues, (long, lat, label, _, rank) => {
    const id = uuidv4();
    onFeature(label, [long, lat], id, rank);
  });
}

export const extractCoordinatesFromDatasets = (
  datasetValues: CatchmentDataset[]
): MarkerPinItem[] => {
  const coordinates: MarkerPinItem[] = [];

  iterateRows(datasetValues, (lng, lat) => {
    const id = uuidv4();
    coordinates.push({ id, lat, lng });
  });

  return coordinates;
};
